import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Key used to store request metadata on HttpErrorResponse */
export const HTTP_REQUEST_METADATA_KEY = '__requestMetadata';

export interface HttpRequestMetadata {
    method: string;
    body: unknown;
}

export type HttpErrorResponseWithMetadata = HttpErrorResponse & {
    [HTTP_REQUEST_METADATA_KEY]?: HttpRequestMetadata;
};

/**
 * Functional HTTP interceptor that attaches sanitized request metadata to errors.
 * This allows the Sentry error extractor to include request body in error reports.
 */
export function sentryHttpInterceptor(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
    return next(request).pipe(
        catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse) {
                // Attach request metadata directly to the error object
                error[HTTP_REQUEST_METADATA_KEY] = {
                    method: request.method,
                    body: sanitizeRequestBody(request.body),
                } as HttpRequestMetadata;
            }
            return throwError(() => error);
        })
    );
}

/**
 * Set of field names (lowercase) that contain sensitive data and should be filtered.
 * These are checked case-insensitively during sanitization.
 */
const SENSITIVE_FIELDS = new Set([
    // Password fields
    'password',
    'password1',
    'password2',
    'new_password',
    'new_password1',
    'new_password2',
    'password_confirmation',
    'current_password',
    'confirmpassword',
    'repeat_password',

    // Authentication tokens
    'token',
    'access_token',
    'refresh_token',
    'access',
    'refresh',
    'uid',
    'client_secret',
    'captcha_token',
    'api_key',
    'apikey',
    'secret',

    // Payment fields
    'credit_card',
    'card_number',
    'cvv',
    'cvc',
]);

/**
 * Sanitizes request body to remove sensitive information before sending to Sentry.
 * Recursively traverses objects and arrays to filter nested sensitive fields.
 *
 * @param value - The value to sanitize (can be any type)
 * @param visited - WeakSet to track visited objects and prevent circular reference loops
 */
function sanitizeRequestBody(
    value: unknown,
    visited = new WeakSet<object>()
): unknown {
    // Handle primitives and null/undefined
    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value !== 'object') {
        return value;
    }

    // Prevent circular reference infinite loops
    if (visited.has(value)) {
        return '[Circular]';
    }
    visited.add(value);

    // Handle arrays - recursively sanitize each element
    if (Array.isArray(value)) {
        return value.map(item => sanitizeRequestBody(item, visited));
    }

    // Handle plain objects - recursively sanitize each property
    const sanitized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
        const keyLower = key.toLowerCase();
        if (SENSITIVE_FIELDS.has(keyLower)) {
            sanitized[key] = '[FILTERED]';
        } else {
            sanitized[key] = sanitizeRequestBody(val, visited);
        }
    }

    return sanitized;
}
